import { Component, OnInit, OnDestroy, ViewChildren, QueryList } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { NgxMaterialTimepickerComponent } from 'ngx-material-timepicker';
import { ToastService } from '../../../../../../services/toast.service';
import { CompanyService } from '../../../../../../services/company/company.service';
import { CommonService } from '../../../../../../services/common/common.service';
import { AuthService } from '../../../../../../services/auth.service';
import { ValidationService } from '../../../../../../services/validation/validation.service';
import { FormChangeTrackerService } from '../../../../../../services/form-change-tracker.service';
import { CalendarService } from '../../../../../../services/calendar/calendar.service';
import { Location } from '@angular/common';
import { lastValueFrom } from 'rxjs';
import { LeaveService as HolidayService } from '../../../../../../services/leave-holiday/leave.service';
import { ShiftMasterService } from '../../../../../../services/shift-master/shift-master.service';
import { EmployeeListService } from '../../../../../../services/employee/employee-list.service';
import { LeaveService } from '../../../../../../services/leave/leave.service';
import { LeaveresumptionService } from '../../../../../../services/leave-resumption/leaveresumption.service';
import Swal from 'sweetalert2';
import { AttendanceRegularizationService } from '../../../../../../services/attendance-regularization/attendance-regularization.service';
import { AttendanceService } from '../../../../../../services/attendance-service/attendance.service';
import moment from 'moment';

@Component({
  selector: 'app-add-attendance-regularization',
  templateUrl: './add-attendance-regularization.component.html',
  styleUrl: './add-attendance-regularization.component.scss'
})
export class AddAttendanceRegularizationComponent implements OnInit, OnDestroy {
  @ViewChildren('startTimePicker') startTimePickers!: QueryList<NgxMaterialTimepickerComponent>;
  @ViewChildren('endTimePicker') endTimePickers!: QueryList<NgxMaterialTimepickerComponent>;

  editForm!: FormGroup;
  // Form and validation properties
  formdata!: FormGroup;
  formKey: string = 'calendarFormKey';
  formisDisable: boolean = false;
  saveClick: boolean = false;

  // Component state properties
  paramValue: any = '';
  viewData: any = '';
  hideCompany: boolean = false;

  // Date properties
  dateFormat: string = 'dd/mm/yy';
  date: any = 'dd-mm-yy'
  angularDateFormat: string = '';
  // List data
  companyList: any = [];

  approvedLeaveList: any = [];

  // Pagination properties
  totalRecords: number = 0;
  page: number = 0;
  currentPage: number = 0;
  pageSize: number = 10;
  size: any = 10;
  search: any = ""

  approverPage: boolean = false;


  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly fb: FormBuilder,
    private readonly toastService: ToastService,
    private readonly companyService: CompanyService,
    private readonly commonService: CommonService,
    private readonly auth: AuthService,
    private readonly validation: ValidationService,
    private readonly formChangeService: FormChangeTrackerService,
    private readonly calendarService: CalendarService,
    private readonly location: Location,
    private readonly holidayService: HolidayService,
    private readonly leaveService: LeaveService,
    private readonly shiftService: ShiftMasterService,
    private readonly emplistService: EmployeeListService,
    private readonly attendanceRegularizationService: AttendanceRegularizationService,
    private readonly attendanceService: AttendanceService,
  ) {
    this.initializeForm();
  }

  async ngOnInit() {
    const currentUrl = this.router.url;
    const hasApprover = currentUrl.includes('approve-attendance-regularization');
    this.approverPage = hasApprover;
    this.setupComponent();

    if (!this.route.snapshot.paramMap.get('id')) {
      const today = new Date();
      this.formdata.patchValue({
        start_date: today,
      });
    }

    this.formdata.valueChanges.subscribe(() => {
      this.formisDisable = this.formChangeService.hasChanged(this.formKey, this.formdata);
    });
  }

  ngOnDestroy(): void {
    this.formChangeService.unregisterForm(this.formKey);
  }

  private initializeForm(): void {
    this.formdata = this.fb.group({
      company_id: [null, [Validators.required]],
      employee_id: [null, [Validators.required]],
      start_date: [null, [Validators.required]],
      end_date: [null, [Validators.required]],
      sections: this.fb.array([]),
      comments: ['']
    });
  }

  get sections(): FormArray {
    return this.formdata.get('sections') as FormArray;
  }

  private setupComponent(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.paramValue = id;
        this.getViewData(id);
      } else {
        this.paramValue = null;
      }
    });
    // Always run these
    this.setupUserPermissions();
    this.getCompanyList();
  }

  private async setupUserPermissions() {
    const userData: any = this.commonService.getLocalStorageData();
    if (userData) {
      if (!userData.client) {
        this.hideCompany = true;
        this.formdata.patchValue({
          company_id: userData.companyID
        });
        await this.getEmployeeList(userData.companyID);
        if (userData.role == '4') {
          this.formdata.patchValue({
            employee_id: userData.userID
          });
          if (!userData.is_help_desk_supervisor) {
            this.formdata.get('employee_id')?.disable();
          }
          // this.getApproveddLeaveListData(userData.userID);
        }
        this.getDateFormat(userData.companyID);
      }
    }
  }

  isFormChanged() {
    this.formisDisable = this.formChangeService.hasChanged(this.formKey, this.formdata);
  }
  renderCalendar = true;

  private async getDateFormat(companyId: any): Promise<void> {
    this.renderCalendar = false;

    try {
      const resp = await lastValueFrom(this.companyService.getParametersById(companyId));
      const dateFormat = resp?.data?.date_format;
      if (dateFormat) {
        this.dateFormat = resp?.data?.date_format;
        this.date = dateFormat;
        // console.log(dateFormat)
        this.angularDateFormat = this.commonService.convertPrimeNGToAngularFormat(dateFormat);
        if (!this.paramValue && !this.route.snapshot.paramMap.get('id')) {
          const today = new Date();
          this.formdata.get('start_date')?.setValue(today);
        }
        this.renderCalendar = true;
      } else {
        this.dateFormat = 'dd/mm/yy';   // fallback format
        this.renderCalendar = true;     // ensure visible
      }
    } catch (err) {
      console.error('Error fetching date format:', err);
    }
  }

  getCompanyList(): void {
    this.companyService.getClientCompanyList().subscribe({
      next: (resp: any) => {
        this.companyList = resp.data;
      },
      error: (err) => {
        console.error('Error fetching company list:', err);
      }
    });
  }


  // getApproveddLeaveListData(id: any): void {
  //   this.attendanceRegularizationService.getApprovedLeaveList({ employee_id: id, status: this.paramValue ? 'approved_and_cancelled' : '' }).subscribe({
  //     next: (resp) => {
  //       this.approvedLeaveList = resp?.data ?? []
  //     }
  //   });
  // }


  getViewData(id: any): void {
    this.attendanceRegularizationService.getRegularizationDataById(id).subscribe({
      next: async (response) => {
        let data = response.data;
        this.viewData = data;
        await this.getEmployeeList(data.company.id);
        await this.getDateFormat(data.company.id);
        // await this.getApproveddLeaveListData(data.employee.id);
        this.formdata.patchValue({
          start_date: new Date(data.start_date) || null,
          end_date: new Date(data.end_date) || null,
          company_id: data.company.id || null,
          employee_id: data.employee.id ?? null,
          comments: data?.comments ?? ''
        });

        // Rebuild sections FormArray with FormGroups
        const sectionsArray = this.formdata.get('sections') as FormArray;
        this.clearFormArray(sectionsArray);
        (data.regularization_details || []).forEach((section: any) => {
          // const isDisabledStatus = ['Present', 'Public Holiday', 'Weekly Off'].includes(section.status);
          const checkIn = section.checkin_time
            ? moment(section.checkin_time, 'HH:mm').format('hh:mm A')
            : null;

          const checkOut = section.checkout_time
            ? moment(section.checkout_time, 'HH:mm').format('hh:mm A')
            : null;

          const sectionGroup = this.fb.group({
            employee_calendar_id: [section.employee_calendar_id || ''],
            calendar_date: [section.calendar_date || '', Validators.required],
            checkin_time: [checkIn || '', [Validators.required]],
            checkout_time: [checkOut || '', [Validators.required]],
            shift_time: [section.shift_time || '---'],
            status: [section.status || '---'],
            comments: [section.comments || '', [
              Validators.required,
              Validators.maxLength(255),
              Validators.pattern(this.validation.letterNumberPlusSlashAnd)
            ]]
          });

          // if (isDisabledStatus) {
          //   sectionGroup.get('checkin_time')?.disable();
          //   sectionGroup.get('checkout_time')?.disable();
          //   sectionGroup.get('comments')?.disable();
          // }

          sectionsArray.push(sectionGroup);
        });

        this.formChangeService.resetForm(this.formKey, this.formdata);
        this.isFormChanged();
        if (this.approverPage || this.paramValue) {
          this.formdata.disable()
          this.formdata.get('comments')?.enable();
        }
      },
      error: (error) => {
        console.error('Error fetching view data:', error);
      }
    });
  }


  onCompanyChange(event: any): void {
    const companyId = event.id;
    if (companyId) {
      this.getDateFormat(companyId);
      this.getEmployeeList(companyId);
    }
    this.formdata.patchValue({
      employee_id: null,
      // start_date: null,
      end_date: null,
      expected_resume_date: null,
      substitute_employee_id: null,
      total_days: '',
    })
  }

  onEmployeeChange(id: any) {
    this.formdata.patchValue({
      leave_id: null
    })
    // this.getApproveddLeaveListData(id);
  }

  employeeList: any = [];
  getEmployeeList(id: any) {
    this.emplistService.getRequestEmployeeList({ company_id: id, show_all_employees: this.paramValue ? '1' : '0' }).subscribe({
      next: (resp: any) => {
        this.employeeList = resp.data;
      }
    });
  }

  getInvalidControls(form: FormGroup = this.formdata, parentKey: string = ''): any[] {
    const invalid: { key: string, value: any, errors: any }[] = [];

    Object.keys(form.controls).forEach(key => {
      const control = form.controls[key];
      const controlKey = parentKey ? `${parentKey}.${key}` : key;

      if (control instanceof FormGroup) {
        invalid.push(...this.getInvalidControls(control, controlKey));
      } else if (control.invalid) {
        invalid.push({
          key: controlKey,
          value: control.value,
          errors: control.errors
        });
      }
    });

    return invalid;
  }


  formSubmit(type: any): void {
    this.saveClick = true;

    if (!this.formdata.invalid) {
      const rawData = this.formdata.getRawValue();
      const trimmedComments = rawData?.comments?.trim() || '';
      this.formdata.patchValue({ comments: trimmedComments });

      const formData = this.formdata.getRawValue();

      // Process sections to ensure times are in 24-hour format
      const sections: any[] = [];
      for (const section of formData.sections) {
        if (!section.checkin_time || !section.checkout_time) {
          sections.push(section);
          continue;
        }

        // Ensure we parse both 12-hour and 24-hour formats
        const checkIn = moment(section.checkin_time, ['HH:mm', 'hh:mm A']);
        const checkOut = moment(section.checkout_time, ['HH:mm', 'hh:mm A']);

        if (!checkIn.isValid() || !checkOut.isValid()) {
          this.toastService.failure('Invalid time format');
          return;
        }

        if (checkIn.format('HH:mm') == checkOut.format('HH:mm')) {
          this.toastService.failure('Check-in and Check-out times cannot be the same.');
          return;
        }

        let duration = moment.duration(checkOut.diff(checkIn)).asHours();
        if (duration < 0) {
          duration += 24; // Crosses midnight
        }

        if (duration > 16) {
          this.toastService.failure('Check-in and Check-out duration cannot exceed 16 hours.');
          return;
        }

        sections.push({
          ...section,
          checkin_time: checkIn.format('HH:mm'),
          checkout_time: checkOut.format('HH:mm'),
        });
      }
      formData.sections = sections;

      if (type === 'Cancelled') {
        formData['status'] = 'Cancelled';
        if (!formData.comments) {
          return this.toastService.failure('Fill the comments');
        }
      }

      formData['start_date'] = moment(formData?.start_date).format('YYYY-MM-DD');
      formData['end_date'] = moment(formData?.end_date).format('YYYY-MM-DD');

      Swal.fire({
        title: `Are you sure you want to ${type}?`,
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
      }).then((result) => {
        if (result.isConfirmed) {
          const apiCall = !this.paramValue
            ? this.attendanceRegularizationService.createRegularization(formData)
            : this.attendanceRegularizationService.updateRegularization(formData, this.paramValue);

          apiCall.subscribe({
            next: (resp: any) => {
              const action = !this.paramValue ? 'created' : 'cancelled';
              this.toastService.success(`Attendance Regularization request ${action} successfully`);
              this.formChangeService.resetForm(this.formKey, this.formdata);
              this.formisDisable = false;
              this.router.navigate(['/app/attendance/apply-attendance-regularization']);
            },
            error: (err: { error: { message: string } }) => {
              this.toastService.failure(err.error.message);
            },
          });
        }
      });
    } else {
      this.markFormGroupTouched();
      const invalidControls = this.getInvalidControls();
      this.toastService.failure('Please fill all required fields');
    }
  }


  private markFormGroupTouched(): void {
    Object.keys(this.formdata.controls).forEach(field => {
      const control = this.formdata.get(field);
      control?.markAsTouched({ onlySelf: true });
    });
  }

  isInvalid(controlName: string): boolean {
    const control = this.formdata.get(controlName);
    if (!control) return false;

    return (
      (this.saveClick && control.invalid) ||
      (this.saveClick && !!control.errors) ||
      (control.invalid && (control.dirty || control.touched))
    );
  }

  checkPermission(type: string): boolean {
    return this.auth.checkPermission(type);
  }

  onCancel(): void {
    this.location.back();
  }

  onTimeChange(event: string, control: AbstractControl) {
    if (event) {
      // Convert time to 24-hour format
      const time24 = moment(event, 'hh:mm A').format('HH:mm');
      control.setValue(time24);
      control.markAsTouched();
    }
  }

  callApprovalApi(type: string, comment: string) {
    this.saveClick = true;
    const rawData = this.formdata.getRawValue();
    let leaveData = this.approvedLeaveList.find((e: any) => e._id == rawData?.leave_id)
    const formData = this.formdata.getRawValue();
    if (type == 'Cancelled') {
      formData.sections = formData.sections
        ?.map((section: any) => {
          if (!section.checkin_time || !section.checkout_time) return section;

          // Ensure we parse both 12-hour and 24-hour formats
          const checkIn = moment(section.checkin_time, ['HH:mm', 'hh:mm A']);
          const checkOut = moment(section.checkout_time, ['HH:mm', 'hh:mm A']);

          if (!checkIn.isValid() || !checkOut.isValid()) {
            this.toastService.failure('Invalid time format');
            return null;
          }

          if (checkIn.format('HH:mm') == checkOut.format('HH:mm')) {
            this.toastService.failure('Check-in and Check-out times cannot be the same.');
            return;
          }

          return {
            ...section,
            checkin_time: checkIn.format('HH:mm'),
            checkout_time: checkOut.format('HH:mm'),
          };
        })
        .filter(Boolean);

      formData['start_date'] = moment(formData?.start_date).format('YYYY-MM-DD');
      formData['end_date'] = moment(formData?.end_date).format('YYYY-MM-DD');
      formData['status'] = 'Cancelled';
      formData['comments'] = comment;

      const apiCall = this.attendanceRegularizationService.updateRegularization(formData, this.paramValue);
      apiCall.subscribe({
        next: (resp: any) => {
          this.toastService.success(`Attendance Regularization request cancelled successfully`);
          this.formChangeService.resetForm(this.formKey, this.formdata);
          this.formisDisable = false;
          this.router.navigate(['/app/attendance/apply-attendance-regularization']);
        },
        error: (err: { error: { message: string } }) => {
          this.toastService.failure(err.error.message);
        },
      });
    }
    else {
      const rawData = this.formdata.getRawValue();
      let leaveData = this.approvedLeaveList.find((e: any) => e._id == rawData.leave_id)
      let updateFormData = {
        ...formData,
        status: type,
        comments: comment,
        workflow_condition: this.viewData?.workflow_condition,
        workflow: this.viewData?.workflow,
        start_date: leaveData?.start_date,
        end_date: leaveData?.end_date,
      }
      const apiCall = this.attendanceRegularizationService.approveOrRejectRegularization(updateFormData, this.paramValue);
      apiCall.subscribe({
        next: (resp: any) => {
          this.toastService.success(`${type} successfully`);
          this.router.navigate(['/app/attendance/approve-attendance-regularization']);
        },
        error: (err: { error: { message: string } }) => {
          this.toastService.failure(err.error.message);
        },
      });
    }
  }

  // --approveOrReject---
  approveOrReject(type: any): void {
    this.saveClick = true;
    const rawData = this.formdata.getRawValue();
    const trimmedComments = rawData?.comments?.trim() || '';

    this.formdata.patchValue({
      comments: trimmedComments
    });

    const formData = this.formdata.getRawValue();
    if (!formData.comments && type == 'Rejected') {
      return this.toastService.failure('Fill the comments');
    }

    let action = type == 'Approved' ? 'Approve' : 'Reject'
    Swal.fire({
      title: `Are you sure you want to ${action}?`,
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    }).then((result) => {
      if (result.isConfirmed) {
        const rawData = this.formdata.getRawValue();

        let leaveData = this.approvedLeaveList.find((e: any) => e._id == rawData.leave_id)

        let formData = {
          status: type,
          comments: rawData.comments,
          workflow_condition: this.viewData?.workflow_condition,
          workflow: this.viewData?.workflow,
          start_date: leaveData?.start_date,
          end_date: leaveData?.end_date,
        }
        const apiCall = this.attendanceRegularizationService.approveOrRejectRegularization(formData, this.paramValue);
        apiCall.subscribe({
          next: (resp: any) => {
            this.toastService.success(`${type} successfully`);
            this.router.navigate(['/app/employee/approve-leave-cancellation']);
          },
          error: (err: { error: { message: string } }) => {
            this.toastService.failure(err.error.message);
          },
        });
      }
    })
  }

  onDateChange(event: any, name: any) {
    if (name == 'start_date') {
      this.formdata.patchValue({
        end_date: ""
      })
      const sectionsArray = this.formdata.get('sections') as FormArray;
      this.clearFormArray(sectionsArray)
    } else {
      this.getRegularizationDataToRequest()
    }
  }

  clearFormArray(formArray: FormArray) {
    while (formArray.length !== 0) {
      formArray.removeAt(0);
    }
  }

  getRegularizationDataToRequest() {
    let data = this.formdata.getRawValue();
    let formData = {
      employee_id: data.employee_id,
      start_date: data.start_date,
      end_date: data.end_date,
    }

    if (data.start_date && data.end_date && data.employee_id) {
      this.attendanceService.getRegularizationDataToRequest(formData).subscribe({
        next: (resp: any) => {
          const sectionsArray = this.formdata.get('sections') as FormArray;
          this.clearFormArray(sectionsArray)

          const startDate = moment(this.formdata.get('start_date')?.value).startOf('day');
          const endDate = moment(this.formdata.get('end_date')?.value).startOf('day');

          for (let currentDate = startDate; currentDate.isSameOrBefore(endDate); currentDate.add(1, 'day')) {
            const formattedDate = currentDate.format('YYYY-MM-DD');

            const existingSection = resp.data && resp.data.length > 0 ? resp.data.find((section: any) =>
              moment(section.calendar_date).format('YYYY-MM-DD') === formattedDate
            ) : '';

            if (existingSection) {
              const shiftStartRaw = existingSection?.shift_config?.start_time || null;
              const shiftEndRaw = existingSection?.shift_config?.end_time || null;

              let shift_time = '---';
              if (shiftStartRaw && shiftEndRaw) {
                const shiftStart = moment(shiftStartRaw, 'HH:mm:ss', true);
                const shiftEnd = moment(shiftEndRaw, 'HH:mm:ss', true);

                if (shiftStart.isValid() && shiftEnd.isValid()) {
                  shift_time = `${shiftStart.format('hh:mm A')} - ${shiftEnd.format('hh:mm A')}`;
                }
              }

              // Handle check-in & check-out
              const checkInMoment = moment.utc(existingSection?.attendance?.checkin_time);
              const checkOutMoment = moment.utc(existingSection?.attendance?.checkout_time);

              const status = existingSection?.attendance?.attendance_status;
              // const isDisabledStatus = ['Present', 'Public Holiday', 'Weekly Off'].includes(status);

              const sectionGroup = this.fb.group({
                employee_calendar_id: [existingSection.employee_calendar_id || ''],
                shift_start_time: [existingSection?.shift_config?.start_time || ''],
                shift_end_time: [existingSection?.shift_config?.end_time || ''],
                calendar_date: [moment(existingSection.calendar_date).format('YYYY-MM-DD') || '', Validators.required],
                checkin_time: [
                  checkInMoment.isValid() ? checkInMoment.format('HH:mm') : '',
                  [Validators.required]
                ],
                checkout_time: [
                  checkOutMoment.isValid() ? checkOutMoment.format('HH:mm') : '',
                  [Validators.required]
                ],
                shift_time: [shift_time],
                status: [status || '---'],
                comments: ['', [
                  Validators.required,
                  Validators.maxLength(255),
                  Validators.pattern(this.validation.letterNumberPlusSlashAnd)
                ]]
              });

              // if (this.paramValue && isDisabledStatus) {
              //   sectionGroup.get('checkin_time')?.disable();
              //   sectionGroup.get('checkout_time')?.disable();
              //   sectionGroup.get('comments')?.disable();
              // }
              sectionsArray.push(sectionGroup);
            }
          }
        }
      })
    }

  }
}
